import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import Confetti from 'react-confetti';
import { Button } from '../../../shared/components/Button';
import { ButtonsGroup } from '../../../shared/components/ButtonsGroup';
import { OllieFloating } from '../../../shared/components/OllieFloating';
import { SpeechBubble } from '../../../shared/components/SpeechBubble';
import { AuthStatus, useAuth } from '../../../shared/hooks/Auth';
import { useLoggedInUserEdit } from '../../../shared/hooks/useLoggedInUserEdit';
import useOcdProfileStore from '../../../systems/user-info/useOcdProfileStore';
import { OnboardingProgress } from '../../../types/obsessless-user';
import { useOnboardingStore } from '../hooks/useOnboardingStore';

interface DrawAHeartReadyProps {
    onContinue: () => void;
    isDisabled: boolean;
    onSpeechBubbleComplete: () => void;
}

export const DrawAHeartReady = ({ onContinue, isDisabled, onSpeechBubbleComplete }: DrawAHeartReadyProps) => {
    const { authStatus, readyUser } = useAuth();
    const hasInitiatedWhatBringsYouResponse = useRef<boolean>(false);
    const whatBringsYouResponse = useOnboardingStore(state => state.whatBringsYouResponse);
    const saveOcdInfoFromUser = useOcdProfileStore(state => state.saveOcdInfoFromUser);
    const { saveOnboardingProgress } = useLoggedInUserEdit();

    const saveWhatBringsYouResponseRef = useRef<Promise<void> | null>(null);

    useEffect(() => {
        const saveWhatBringsYouResponse = async () => {
            if (authStatus !== AuthStatus.AuthenticatedReady || !readyUser || hasInitiatedWhatBringsYouResponse.current) {
                return;
            }
            hasInitiatedWhatBringsYouResponse.current = true;
            await saveOcdInfoFromUser(readyUser.uid, 'what_brings_you', whatBringsYouResponse);
        };
        saveWhatBringsYouResponseRef.current = saveWhatBringsYouResponse();
    }, [authStatus, readyUser, whatBringsYouResponse, saveOcdInfoFromUser]);


    const onContinueHandler = async () => {
        if (saveWhatBringsYouResponseRef.current) {
            await saveWhatBringsYouResponseRef.current;
        }
        await saveOnboardingProgress(OnboardingProgress.DrawnHeart);
        await onContinue();
    }

    return (
        <div className='flex flex-col gap-10 safe-pb-4 px-8 flex-1 overflow-hidden'>
            <title>Extra Courage?</title>
            <div className="absolute inset-0 z-[-1] pointer-events-none">
                <Confetti
                    width={window.innerWidth}
                    height={window.innerHeight}
                    confettiSource={{
                        x: window.innerWidth / 2,
                        y: window.innerHeight - 100,
                        w: 50,
                        h: 0
                    }}
                    drawShape={ctx => {
                        const transform = ctx.getTransform();
                        ctx.setTransform(1, 0, 0, 1, transform.e, transform.f);
                        ctx.font = "40px Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji";
                        ctx.fillText("💪", -20, 20)
                    }}
                    initialVelocityY={30}
                    initialVelocityX={10}
                    gravity={0.3}
                    numberOfPieces={10}
                    recycle={false}
                    tweenDuration={300}
                />
            </div>
            <div className='flex-1 flex flex-col justify-center items-center'>
                <div className='flex flex-col items-center justify-end overflow-show h-[250px]'>
                    <SpeechBubble
                        text="Let's start with a couple questions about your OCD to help me understand you better."
                        onTypingComplete={onSpeechBubbleComplete}
                    />
                    <OllieFloating />
                </div>
            </div>
            <div className="flex flex-col items-center justify-end gap-4 flex-shrink-0 relative z-10 min-h-[260px]">
                <div className="w-full flex flex-col justify-end">
                    <motion.div
                        className="text-osGrey-2 text-balance text-center px-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                    >
                        Don't worry, your data will stay completely secure!
                    </motion.div>
                </div>
                <ButtonsGroup animated={false}>
                    <Button
                        inactive={isDisabled}
                        onClick={onContinueHandler}
                        label="Alrighty"
                    />
                </ButtonsGroup>
            </div>
        </div>
    );
};
